import { Vector2, Vector3 } from "three";
import { Obj } from "../internal";
import { PROJECTION, STREAMTYPE } from "./internal";
/**
 * Container for all imageobservation data
 * @class ImageObservation
 * @author Benjamin Loesch
 * @extends Obj
 * @constructor
 */
export class ImageObservation extends Obj {
    constructor(geoframe, sensor_x, sensor_y, focallength, id, sensorid, faceorientation, cubefaceindex) {
        super();
        this.excludeForCalc = false;
        this.geoframe = geoframe;
        this.x = sensor_x;
        this.y = sensor_y;
        this.focallength = focallength;
        this.id = id;
        this.sensorid = sensorid;
        this.streamtype = geoframe.getMeasureInfo().streamtype;
        this.projection = PROJECTION.PERSPECTIVE;
        if (this.streamtype == STREAMTYPE.CALOTTE)
            this.projection = PROJECTION.PANORAMIC;
        this.pixelsize = geoframe.getMeasureInfo().imagemeta.pixsize;
        const mh = this.geoframe.scene.getMeasureHandler();
        let ori;
        if (this.streamtype == STREAMTYPE.CUBEMAP) {
            this.faceorientation = faceorientation;
            this.faceindex = cubefaceindex;
            ori = mh.getWorldOriForSensorId(sensorid, geoframe, faceorientation);
        }
        else {
            this.faceindex = sensorid;
            ori = mh.getWorldOriForSensorId(sensorid, geoframe);
        }
        this.sensoreasting = ori[0];
        this.sensornorthing = ori[1];
        this.sensorheight = ori[2];
        this.sensoromega = ori[3];
        this.sensorphi = ori[4];
        this.sensorkappa = ori[5];
        this.scene = geoframe.scene;
        this.updateImageCoor();
    }
    updateByReprojection(pos) {
        const R = this.geoframe.getRotationMatrix();
        const X0 = [this.geoframe.easting, this.geoframe.northing, this.geoframe.height];
        const X = [pos.x, pos.y, pos.z];
        const ck = this.focallength;
        const kx = R[0][0] * (X[0] - X0[0]) + R[1][0] * (X[1] - X0[1]) + R[2][0] * (X[2] - X0[2]);
        const ky = R[0][1] * (X[0] - X0[0]) + R[1][1] * (X[1] - X0[1]) + R[2][1] * (X[2] - X0[2]);
        const N = R[0][2] * (X[0] - X0[0]) + R[1][2] * (X[1] - X0[1]) + R[2][2] * (X[2] - X0[2]);
        const xsb = (-ck * kx) / N;
        const ysb = (-ck * ky) / N;
        this.x_imgcoor = xsb;
        this.y_imgcoor = ysb;
        this.z_imgcoor = ck;
        const senscoor = this.scene.getMeasureHandler().imageToSensor(new Vector2(xsb, ysb));
        this.x = senscoor.x;
        this.y = senscoor.y;
    }
    setSensCoor(x, y) {
        this.x = x;
        this.y = y;
        this.updateImageCoor();
    }
    /**
     * calc pixel [x,y] to image coordinates
     */
    updateImageCoor() {
        const imgcoor = this.geoframe.scene
            .getMeasureHandler()
            .sensorToImage(new Vector2(this.x, this.y), this.geoframe);
        this.x_imgcoor = imgcoor.x;
        this.y_imgcoor = imgcoor.y;
    }
    getEpipolarLine(anothergeoframe, sensorid, faceorientation) {
        //Calc line in 3D
        const P = new Vector3(this.sensoreasting, this.sensornorthing, this.sensorheight);
        let q = this.scene
            .getMeasureHandler()
            .imageToModel(new Vector2(this.x_imgcoor, this.y_imgcoor), null, this.sensorid, this.geoframe);
        let Q;
        if (this.streamtype == STREAMTYPE.CUBEMAP) {
            Q = this.scene.getMeasureHandler().modelToWorld(q, this.sensorid, this.geoframe, this.faceorientation);
        }
        else {
            Q = this.scene.getMeasureHandler().modelToWorld(q, this.sensorid, this.geoframe);
        }
        //Epipolar line in 3D Space is: P+a*(Q-P) -> a = arbitrary
        //Project P and Q to image
        if (!sensorid)
            sensorid = 0;
        let p;
        if (anothergeoframe.getMeasureInfo().streamtype == STREAMTYPE.CUBEMAP) {
            p = this.scene.getMeasureHandler().worldToModel(P, sensorid, anothergeoframe, faceorientation);
            q = this.scene.getMeasureHandler().worldToModel(Q, sensorid, anothergeoframe, faceorientation);
        }
        else {
            p = this.scene.getMeasureHandler().worldToModel(P, sensorid, anothergeoframe);
            q = this.scene.getMeasureHandler().worldToModel(Q, sensorid, anothergeoframe);
        }
        const pimage = this.scene.getMeasureHandler().modelToImage(p, sensorid, anothergeoframe);
        const qimage = this.scene.getMeasureHandler().modelToImage(q, sensorid, anothergeoframe);
        const psens = this.scene.getMeasureHandler().imageToSensor(pimage, anothergeoframe);
        const qsens = this.scene.getMeasureHandler().imageToSensor(qimage, anothergeoframe);
        //line in anothergeoframe image plane is p+a(q-p) -> a=arbitrary
        //p : projection center of this image projected in other image
        //q : point one meter from this image on view ray
        return [psens, qsens];
    }
}
//# sourceMappingURL=imageobservation.js.map